import React, { useState } from 'react';
import RolesChecker from 'components/utils/RolesChecker';
import { Roles } from 'constants/literals';
import { useTranslation } from 'react-i18next';
import { Button, FormGroup, Col, Label } from 'reactstrap';
import FormControl from 'components/utils/FormControl';
import { compose } from 'recompose';
import { ApiDataBinding, withApiData } from 'react-api-data';
import { ApiResponse, MailingList, Omit, Exchanges } from 'constants/api';
import { ExchangeFormikSelect } from 'components/utils/ExchangeSelect';
import { connect } from 'react-redux';
import { ReduxState } from 'createStore';
import { getAllExchangesSelector } from 'redux/apiData/apiDataSelectors';
import { object, string } from 'yup';
import FormModal from './FormModal';
import { withRouter, RouteComponentProps } from 'react-router';
import { getLinkToMailingList } from 'utils/linksUtils';
import { createEmailAddressListValidation } from 'validation';

interface EnhanceProps {
    createMailingListBinding: ApiDataBinding<ApiResponse<MailingList>>;
    exchangesData: Exchanges;
}

type Props = EnhanceProps & RouteComponentProps;

type FormValues = Omit<MailingList, 'id' | 'uuid' | 'exchange_id' | 'exchange_uuid'> & { exchange: string };

const enhance = compose<Props, {}>(
    withApiData({ createMailingListBinding: 'postMailingLists' }),
    connect((state: ReduxState) => ({
        exchangesData: getAllExchangesSelector(state.apiData),
    })),
    withRouter
);

function MailingListAddModal(props: Props) {
    const { createMailingListBinding, exchangesData, history } = props;
    const [isOpen, setIsOpen] = useState(false);
    const { t } = useTranslation();

    function toggle() {
        setIsOpen((isOpen) => !isOpen);
    }

    function onSubmit(values: FormValues) {
        return createMailingListBinding.perform(undefined, values);
    }

    function handleSuccess(binding: typeof createMailingListBinding) {
        const mailingList = binding.data!.data!;
        history.push(getLinkToMailingList(mailingList.uuid));
    }

    return (
        <React.Fragment>
            <FormModal
                header={t('mailingListAddModal.title')}
                isOpen={isOpen}
                toggle={toggle}
                initialValues={{
                    exchange: '',
                    name: '',
                    description: '',
                    list_url: '',
                    archives_url: '',
                    contact_email: '',
                }}
                validationSchema={object({
                    exchange: string().required(t('validation.requiredField')),
                    name: string().required(t('validation.requiredField')),
                    description: string().required(t('validation.requiredField')),
                    list_url: string().url(t('validation.invalidUrl')),
                    archives_url: string().url(t('validation.invalidUrl')),
                    contact_email: createEmailAddressListValidation(t),
                })}
                onSubmit={onSubmit}
                onSuccess={handleSuccess}
                successMessage={t('mailingListAddModal.successMessage')}
            >
                {({ errors, touched }) => (
                    <React.Fragment>
                        <FormGroup row>
                            <Col md={3}>
                                <Label htmlFor="exchange">{t('common.exchange')}</Label>
                            </Col>
                            <Col md={9}>
                                <ExchangeFormikSelect
                                    exchanges={exchangesData}
                                    name="exchange"
                                    errors={errors}
                                    touched={touched}
                                />
                            </Col>
                        </FormGroup>
                        <FormGroup row>
                            <Col md={3}>
                                <Label htmlFor="name">{t('mailingList.name')}</Label>
                            </Col>
                            <Col md={9}>
                                <FormControl type="text" name="name" errors={errors} touched={touched} />
                            </Col>
                        </FormGroup>
                        <FormGroup row>
                            <Col md={3}>
                                <Label htmlFor="description">{t('mailingList.description')}</Label>
                            </Col>
                            <Col md={9}>
                                <FormControl type="text" name="description" errors={errors} touched={touched} />
                            </Col>
                        </FormGroup>
                        <FormGroup row>
                            <Col md={3}>
                                <Label htmlFor="list_url">{t('mailingList.listUrl')}</Label>
                            </Col>
                            <Col md={9}>
                                <FormControl type="url" name="list_url" errors={errors} touched={touched} />
                            </Col>
                        </FormGroup>
                        <FormGroup row>
                            <Col md={3}>
                                <Label htmlFor="archives_url">{t('mailingList.archivesUrl')}</Label>
                            </Col>
                            <Col md={9}>
                                <FormControl type="url" name="archives_url" errors={errors} touched={touched} />
                            </Col>
                        </FormGroup>
                        <FormGroup row>
                            <Col md={3}>
                                <Label htmlFor="contact_email">{t('mailingList.contactEmail')}</Label>
                            </Col>
                            <Col md={9}>
                                <FormControl type="text" name="contact_email" errors={errors} touched={touched} />
                            </Col>
                        </FormGroup>
                    </React.Fragment>
                )}
            </FormModal>
            <RolesChecker roles={[Roles.admin, Roles.noc]}>
                <Button color="light" onClick={() => setIsOpen(true)}>
                    {t('mailingListAddModal.addBtn')}
                </Button>
            </RolesChecker>
        </React.Fragment>
    );
}

export default enhance(MailingListAddModal);
